import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlug,
  faCheck,
  faTimes,
  faExclamationTriangle,
  faInfoCircle,
  faExternalLinkAlt,
  faKey,
  faShield,
  faChartLine,
  faUsers,
  faEye,
  faHeart,
  faComment,
  faShare,
  faBolt
} from '@fortawesome/free-solid-svg-icons';
import { 
  faGoogle,
  faFacebook,
  faTwitter,
  faLinkedin,
  faInstagram,
  faTiktok,
  faYoutube,
  faPinterest,
  faSnapchat,
  faShopify,
  faWordpress,
  faMailchimp,
  faHubspot,
  faSalesforce
} from '@fortawesome/free-brands-svg-icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

const IntegrationsPage: React.FC = () => {
  const { business } = useAuth();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Helper function to format relative time
  const formatRelativeTime = (date: Date | string | null | undefined): string | null => {
    if (!date) return null;
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return null;
    
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Az önce';
    if (diffMins < 60) return `${diffMins} dakika önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;
    return dateObj.toLocaleDateString('tr-TR');
  };

  // Format date for display
  const formatDate = (date: Date | string | null | undefined): string | null => {
    if (!date) return null;
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return null;
    return dateObj.toLocaleDateString('tr-TR');
  };

  // Get social media integrations from business data
  const socialMediaIntegrations = useMemo(() => {
    if (!business) return [];
    
    const integrations = [];
    
    // Instagram
    if (business.instagram) {
      const isConnected = business.instagram.isConnected || !!(business.instagram.username && business.instagram.username.trim());
      integrations.push({
      id: 'instagram-business',
      name: 'Instagram Business',
        username: business.instagram.username,
        description: business.instagram.username 
          ? `@${business.instagram.username} - Instagram hesabınızın detaylı analitik verilerini alın`
          : 'Instagram hesabınızın detaylı analitik verilerini alın',
      category: 'social',
      icon: faInstagram,
      color: 'text-pink-500',
      bgColor: 'bg-pink-500/20',
        status: isConnected 
          ? (business.instagram.syncStatus === 'failed' ? 'error' : business.instagram.syncStatus === 'pending' ? 'pending' : 'connected')
          : 'pending',
        connectedAt: business.instagram.connectedAt ? formatDate(business.instagram.connectedAt) : null,
        lastSync: business.instagram.syncAt ? formatRelativeTime(business.instagram.syncAt) : null,
        syncError: business.instagram.syncError || null,
      benefits: ['İçerik performansı', 'Audience insights', 'Hashtag analizi'],
      metrics: ['Reach', 'Impressions', 'Engagement rate']
      });
    }
    
    // Facebook
    if (business.facebook) {
      const isConnected = business.facebook.isConnected || !!(business.facebook.username && business.facebook.username.trim());
      integrations.push({
      id: 'facebook-pages',
      name: 'Facebook Pages',
        username: business.facebook.username,
        description: business.facebook.username
          ? `@${business.facebook.username} - Facebook sayfanızın performans metriklerini takip edin`
          : 'Facebook sayfanızın performans metriklerini takip edin',
      category: 'social',
      icon: faFacebook,
      color: 'text-blue-600',
      bgColor: 'bg-blue-600/20',
        status: isConnected
          ? (business.facebook.syncStatus === 'failed' ? 'error' : business.facebook.syncStatus === 'pending' ? 'pending' : 'connected')
          : 'pending',
        connectedAt: business.facebook.connectedAt ? formatDate(business.facebook.connectedAt) : null,
        lastSync: business.facebook.syncAt ? formatRelativeTime(business.facebook.syncAt) : null,
        syncError: business.facebook.syncError || null,
      benefits: ['Sayfa insights', 'Post performansı', 'Audience analizi'],
      metrics: ['Page likes', 'Post reach', 'Engagement']
      });
    }
    
    // Twitter
    if (business.twitter) {
      const isConnected = business.twitter.isConnected || !!(business.twitter.username && business.twitter.username.trim());
      integrations.push({
      id: 'twitter-analytics',
        name: 'Twitter/X Analytics',
        username: business.twitter.username,
        description: business.twitter.username
          ? `@${business.twitter.username} - Tweet performansınızı ve takipçi analizlerinizi görün`
          : 'Tweet performansınızı ve takipçi analizlerinizi görün',
      category: 'social',
      icon: faTwitter,
      color: 'text-blue-400',
      bgColor: 'bg-blue-400/20',
        status: isConnected
          ? (business.twitter.syncStatus === 'failed' ? 'error' : business.twitter.syncStatus === 'pending' ? 'pending' : 'connected')
          : 'pending',
        connectedAt: business.twitter.connectedAt ? formatDate(business.twitter.connectedAt) : null,
        lastSync: business.twitter.syncAt ? formatRelativeTime(business.twitter.syncAt) : null,
        syncError: business.twitter.syncError || null,
      benefits: ['Tweet analizi', 'Takipçi büyümesi', 'Hashtag performansı'],
      metrics: ['Tweet impressions', 'Engagement rate', 'Follower growth']
      });
    }
    
    // LinkedIn
    if (business.linkedin) {
      const isConnected = business.linkedin.isConnected || !!(business.linkedin.username && business.linkedin.username.trim());
      integrations.push({
      id: 'linkedin-analytics',
      name: 'LinkedIn Analytics',
        username: business.linkedin.username,
        description: business.linkedin.username
          ? `${business.linkedin.username} - LinkedIn içeriklerinizin profesyonel performansını analiz edin`
          : 'LinkedIn içeriklerinizin profesyonel performansını analiz edin',
      category: 'social',
      icon: faLinkedin,
      color: 'text-blue-700',
      bgColor: 'bg-blue-700/20',
        status: isConnected
          ? (business.linkedin.syncStatus === 'failed' ? 'error' : business.linkedin.syncStatus === 'pending' ? 'pending' : 'connected')
          : 'pending',
        connectedAt: business.linkedin.connectedAt ? formatDate(business.linkedin.connectedAt) : null,
        lastSync: business.linkedin.syncAt ? formatRelativeTime(business.linkedin.syncAt) : null,
        syncError: business.linkedin.syncError || null,
        benefits: ['Content performance', 'Professional insights', 'Network growth'],
        metrics: ['Post views', 'Engagement', 'Profile views']
      });
    }
    
    // TikTok
    if (business.tiktok) {
      const isConnected = business.tiktok.isConnected || !!(business.tiktok.username && business.tiktok.username.trim());
      integrations.push({
        id: 'tiktok-analytics',
        name: 'TikTok Analytics',
        username: business.tiktok.username,
        description: business.tiktok.username
          ? `@${business.tiktok.username} - TikTok içeriklerinizin performansını ve takipçi analizlerinizi görün`
          : 'TikTok içeriklerinizin performansını ve takipçi analizlerinizi görün',
        category: 'social',
        icon: faTiktok,
        color: 'text-white',
        bgColor: 'bg-gradient-to-br from-pink-500/20 to-purple-500/20',
        status: isConnected
          ? (business.tiktok.syncStatus === 'failed' ? 'error' : business.tiktok.syncStatus === 'pending' ? 'pending' : 'connected')
          : 'pending',
        connectedAt: business.tiktok.connectedAt ? formatDate(business.tiktok.connectedAt) : null,
        lastSync: business.tiktok.syncAt ? formatRelativeTime(business.tiktok.syncAt) : null,
        syncError: business.tiktok.syncError || null,
        benefits: ['Video performansı', 'Trend analizi', 'Takipçi büyümesi'],
        metrics: ['Video views', 'Engagement rate', 'Follower growth']
      });
    }
    
    return integrations;
  }, [business]);

  // Other integrations (analytics, e-commerce, etc.) - keeping mock data for now
  const otherIntegrations = useMemo(() => [
    // Analytics
    {
      id: 'google-analytics',
      name: 'Google Analytics',
      username: undefined,
      description: t('integrations.otherIntegrations.googleAnalytics.description'),
      category: 'analytics',
      icon: faGoogle,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/20',
      status: 'pending' as const,
      connectedAt: null,
      lastSync: null,
      syncError: null,
      benefits: [
        t('integrations.otherIntegrations.googleAnalytics.benefits.0'),
        t('integrations.otherIntegrations.googleAnalytics.benefits.1'),
        t('integrations.otherIntegrations.googleAnalytics.benefits.2')
      ],
      metrics: [
        t('integrations.otherIntegrations.googleAnalytics.metrics.0'),
        t('integrations.otherIntegrations.googleAnalytics.metrics.1'),
        t('integrations.otherIntegrations.googleAnalytics.metrics.2')
      ]
    },

    // E-commerce
    {
      id: 'shopify',
      name: 'Shopify',
      username: undefined,
      description: t('integrations.otherIntegrations.shopify.description'),
      category: 'ecommerce',
      icon: faShopify,
      color: 'text-green-600',
      bgColor: 'bg-green-600/20',
      status: 'pending' as const,
      connectedAt: null,
      lastSync: null,
      syncError: null,
      benefits: [
        t('integrations.otherIntegrations.shopify.benefits.0'),
        t('integrations.otherIntegrations.shopify.benefits.1'),
        t('integrations.otherIntegrations.shopify.benefits.2')
      ],
      metrics: [
        t('integrations.otherIntegrations.shopify.metrics.0'),
        t('integrations.otherIntegrations.shopify.metrics.1'),
        t('integrations.otherIntegrations.shopify.metrics.2')
      ]
    },
    {
      id: 'woocommerce',
      name: 'WooCommerce',
      username: undefined,
      description: t('integrations.otherIntegrations.woocommerce.description'),
      category: 'ecommerce',
      icon: faWordpress,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/20',
      status: 'pending' as const,
      connectedAt: null,
      lastSync: null,
      syncError: null,
      benefits: [
        t('integrations.otherIntegrations.woocommerce.benefits.0'),
        t('integrations.otherIntegrations.woocommerce.benefits.1'),
        t('integrations.otherIntegrations.woocommerce.benefits.2')
      ],
      metrics: [
        t('integrations.otherIntegrations.woocommerce.metrics.0'),
        t('integrations.otherIntegrations.woocommerce.metrics.1'),
        t('integrations.otherIntegrations.woocommerce.metrics.2')
      ]
    },

    // Marketing
    {
      id: 'mailchimp',
      name: 'Mailchimp',
      username: undefined,
      description: t('integrations.otherIntegrations.mailchimp.description'),
      category: 'marketing',
      icon: faMailchimp,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500/20',
      status: 'pending' as const,
      connectedAt: null,
      lastSync: null,
      syncError: null,
      benefits: [
        t('integrations.otherIntegrations.mailchimp.benefits.0'),
        t('integrations.otherIntegrations.mailchimp.benefits.1'),
        t('integrations.otherIntegrations.mailchimp.benefits.2')
      ],
      metrics: [
        t('integrations.otherIntegrations.mailchimp.metrics.0'),
        t('integrations.otherIntegrations.mailchimp.metrics.1'),
        t('integrations.otherIntegrations.mailchimp.metrics.2')
      ]
    },
    {
      id: 'hubspot',
      name: 'HubSpot',
      username: undefined,
      description: t('integrations.otherIntegrations.hubspot.description'),
      category: 'marketing',
      icon: faHubspot,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/20',
      status: 'pending' as const,
      connectedAt: null,
      lastSync: null,
      syncError: null,
      benefits: [
        t('integrations.otherIntegrations.hubspot.benefits.0'),
        t('integrations.otherIntegrations.hubspot.benefits.1'),
        t('integrations.otherIntegrations.hubspot.benefits.2')
      ],
      metrics: [
        t('integrations.otherIntegrations.hubspot.metrics.0'),
        t('integrations.otherIntegrations.hubspot.metrics.1'),
        t('integrations.otherIntegrations.hubspot.metrics.2')
      ]
    },

    // Automation
    {
      id: 'zapier',
      name: 'Zapier',
      username: undefined,
      description: t('integrations.otherIntegrations.zapier.description'),
      category: 'automation',
      icon: faBolt,
      color: 'text-orange-400',
      bgColor: 'bg-orange-400/20',
      status: 'pending' as const,
      connectedAt: null,
      lastSync: null,
      syncError: null,
      benefits: [
        t('integrations.otherIntegrations.zapier.benefits.0'),
        t('integrations.otherIntegrations.zapier.benefits.1'),
        t('integrations.otherIntegrations.zapier.benefits.2')
      ],
      metrics: [
        t('integrations.otherIntegrations.zapier.metrics.0'),
        t('integrations.otherIntegrations.zapier.metrics.1'),
        t('integrations.otherIntegrations.zapier.metrics.2')
      ]
    },
    {
      id: 'salesforce',
      name: 'Salesforce',
      username: undefined,
      description: t('integrations.otherIntegrations.salesforce.description'),
      category: 'automation',
      icon: faSalesforce,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/20',
      status: 'pending' as const,
      connectedAt: null,
      lastSync: null,
      syncError: null,
      benefits: [
        t('integrations.otherIntegrations.salesforce.benefits.0'),
        t('integrations.otherIntegrations.salesforce.benefits.1'),
        t('integrations.otherIntegrations.salesforce.benefits.2')
      ],
      metrics: [
        t('integrations.otherIntegrations.salesforce.metrics.0'),
        t('integrations.otherIntegrations.salesforce.metrics.1'),
        t('integrations.otherIntegrations.salesforce.metrics.2')
      ]
    }
  ], [t]);

  // Combine social media integrations with other integrations
  const integrations = useMemo(() => {
    return [...socialMediaIntegrations, ...otherIntegrations];
  }, [socialMediaIntegrations, otherIntegrations]);

  const categories = [
    { value: 'all', label: t('integrations.categories.all') },
    { value: 'analytics', label: t('integrations.categories.analytics') },
    { value: 'social', label: t('integrations.categories.social') },
    { value: 'ecommerce', label: t('integrations.categories.ecommerce') },
    { value: 'marketing', label: t('integrations.categories.marketing') },
    { value: 'automation', label: t('integrations.categories.automation') }
  ];

  const filteredIntegrations = integrations.filter(integration => {
    const matchesSearch = integration.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         integration.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || integration.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected':
        return <FontAwesomeIcon icon={faCheck} className="text-green-400" />;
      case 'pending':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-400" />;
      case 'error':
        return <FontAwesomeIcon icon={faTimes} className="text-red-400" />;
      default:
        return <FontAwesomeIcon icon={faPlug} className="text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'connected':
        return t('integrations.status.connected');
      case 'pending':
        return t('integrations.status.pending');
      case 'error':
        return t('integrations.status.error');
      default:
        return t('integrations.status.notConnected');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'bg-green-500/20 text-green-400';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'error':
        return 'bg-red-500/20 text-red-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{t('integrations.title')}</h1>
          <p className="text-white/70 text-sm md:text-base">{t('integrations.description')}</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder={t('integrations.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm md:text-base"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category.value}
              onClick={() => setSelectedCategory(category.value)}
              className={`px-3 md:px-4 py-2 rounded-lg transition-all text-sm md:text-base ${
                selectedCategory === category.value
                  ? 'bg-purple-600 text-white'
                  : 'bg-white/10 text-white/70 hover:text-white hover:bg-white/20'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
              <FontAwesomeIcon icon={faCheck} className="text-green-400 text-xl" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">
                {integrations.filter(i => i.status === 'connected').length}
              </h3>
              <p className="text-white/70 text-sm">{t('integrations.stats.connected')}</p>
            </div>
          </div>
        </div>
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-400 text-xl" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">
                {integrations.filter(i => i.status === 'pending').length}
              </h3>
              <p className="text-white/70 text-sm">{t('integrations.stats.pending')}</p>
            </div>
          </div>
        </div>
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
              <FontAwesomeIcon icon={faTimes} className="text-red-400 text-xl" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">
                {integrations.filter(i => i.status === 'error').length}
              </h3>
              <p className="text-white/70 text-sm">{t('integrations.stats.error')}</p>
            </div>
          </div>
        </div>
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <FontAwesomeIcon icon={faPlug} className="text-blue-400 text-xl" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">{integrations.length}</h3>
              <p className="text-white/70 text-sm">{t('integrations.stats.total')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredIntegrations.map((integration) => (
          <div key={integration.id} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-4 md:p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${integration.bgColor}`}>
                  <FontAwesomeIcon icon={integration.icon} className={`text-xl ${integration.color}`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold text-lg">{integration.name}</h3>
                  {integration.username && (
                    <p className="text-white/60 text-xs mt-1">@{integration.username}</p>
                  )}
                  <span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs ${getStatusColor(integration.status)}`}>
                    {getStatusIcon(integration.status)} {getStatusText(integration.status)}
                  </span>
                </div>
              </div>
              <button className="text-white/50 hover:text-white">
                <FontAwesomeIcon icon={faInfoCircle} />
              </button>
            </div>

            <p className="text-white/70 text-sm mb-4 leading-relaxed">
              {integration.description}
            </p>

            {/* Benefits */}
            <div className="mb-4">
              <h4 className="text-white font-medium text-sm mb-2">{t('integrations.benefits')}</h4>
              <div className="flex flex-wrap gap-1">
                {integration.benefits.map((benefit, index) => (
                  <span key={index} className="text-xs px-2 py-1 bg-white/10 rounded text-white/70">
                    {benefit}
                  </span>
                ))}
              </div>
            </div>

            {/* Metrics */}
            <div className="mb-4">
              <h4 className="text-white font-medium text-sm mb-2">{t('integrations.metrics')}</h4>
              <div className="flex flex-wrap gap-1">
                {integration.metrics.map((metric, index) => (
                  <span key={index} className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded">
                    {metric}
                  </span>
                ))}
              </div>
            </div>

            {/* Connection Info */}
            {(integration.status === 'connected' || integration.status === 'error') && (
              <div className="mb-4 space-y-2">
                {integration.connectedAt && (
                <div className="flex items-center justify-between text-white/70 text-xs">
                    <span>{t('integrations.connectionDate')}</span>
                  <span>{integration.connectedAt}</span>
                </div>
                )}
                {integration.lastSync && (
                <div className="flex items-center justify-between text-white/70 text-xs">
                    <span>{t('integrations.lastSync')}</span>
                  <span>{integration.lastSync}</span>
                </div>
                )}
                {integration.syncError && (
                  <div className="flex items-center justify-between text-red-400 text-xs">
                    <span>{t('integrations.error')}</span>
                    <span className="text-right">{integration.syncError}</span>
                  </div>
                )}
              </div>
            )}

            {/* Action Button */}
            <button
              className={`w-full py-2 rounded-lg font-medium transition-all ${
                integration.status === 'connected'
                  ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                  : integration.status === 'error'
                  ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                  : 'bg-gradient-to-r from-purple-500 to-purple-700 text-white hover:opacity-90'
              }`}
            >
              {integration.status === 'connected' ? (
                <>
                  <FontAwesomeIcon icon={faTimes} className="mr-2" />
                  {t('integrations.disconnect')}
                </>
              ) : integration.status === 'error' ? (
                <>
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                  {t('integrations.reconnect')}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlug} className="mr-2" />
                  {t('integrations.connect')}
                </>
              )}
            </button>
          </div>
        ))}
      </div>

      {/* No Results */}
      {filteredIntegrations.length === 0 && (
        <div className="text-center py-20">
          <FontAwesomeIcon icon={faPlug} className="text-white/30 text-6xl mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">{t('integrations.noResults')}</h3>
          <p className="text-white/70">{t('integrations.noResultsDescription')}</p>
        </div>
      )}
    </div>
  );
};

export default IntegrationsPage;
